import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { translations, supportedLanguages } from '@/i18n/translations';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const { token, user, setUser } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Initialize from localStorage first for immediate display
    const savedLang = localStorage.getItem('treppan_language');
    return (savedLang && translations[savedLang]) ? savedLang : 'en';
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch language preference from server when token is available
  useEffect(() => {
    const fetchLanguagePreference = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/users/language?token=${token}`);
          const serverLang = response.data.language;
          if (serverLang && translations[serverLang]) {
            setCurrentLanguage(serverLang);
            localStorage.setItem('treppan_language', serverLang);
            applyLanguageDirection(serverLang);
          }
        } catch (error) {
          console.error('Failed to fetch language preference:', error);
        }
      }
      setIsLoading(false);
    };

    fetchLanguagePreference();
  }, [token]);

  // Apply direction on initial load
  useEffect(() => {
    applyLanguageDirection(currentLanguage);
  }, [currentLanguage]);

  // Apply RTL direction for Arabic
  const applyLanguageDirection = (langCode) => {
    const dir = translations[langCode]?.dir || 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = langCode;
    
    // Add/remove RTL class for styling
    if (dir === 'rtl') {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
  };

  // Change language and save to user profile
  const changeLanguage = useCallback(async (langCode) => {
    if (!translations[langCode]) {
      console.error(`Language ${langCode} not supported`);
      return false;
    }

    setCurrentLanguage(langCode);
    applyLanguageDirection(langCode);
    localStorage.setItem('treppan_language', langCode);

    // Save to user profile if logged in
    if (token) {
      try {
        const response = await axios.put(
          `${API}/users/language?token=${token}`,
          { language: langCode }
        );
        
        if (response.data.success && setUser) {
          setUser(prev => ({ ...prev, language: langCode }));
        }
      } catch (error) {
        console.error('Failed to save language preference:', error);
        // Language is still changed locally even if save fails
      }
    }

    return true;
  }, [token, setUser]);

  // Get translation function
  const t = useCallback((key, defaultValue = '') => {
    const keys = key.split('.');
    let value = translations[currentLanguage];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fall back to English
        value = translations.en;
        for (const ek of keys) {
          if (value && typeof value === 'object' && ek in value) {
            value = value[ek];
          } else {
            return defaultValue || key;
          }
        }
        return value;
      }
    }
    
    return value || defaultValue || key;
  }, [currentLanguage]);

  // Get current language info
  const getCurrentLanguageInfo = useCallback(() => {
    return {
      code: currentLanguage,
      name: translations[currentLanguage]?.name || 'English',
      dir: translations[currentLanguage]?.dir || 'ltr',
    };
  }, [currentLanguage]);

  // Check if current language is RTL
  const isRTL = translations[currentLanguage]?.dir === 'rtl';

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    isRTL,
    supportedLanguages,
    getCurrentLanguageInfo,
    isLoading,
    translations: translations[currentLanguage],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
